import { useEffect, useMemo, useState } from "react";
import { TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { listOrdner, listSOrdner, listYears, type OrdnerOut, type SOrdnerOut } from "@/lib/projects";

export interface DocumentTemplateMetaValue {
  schlagworte: string[];
  ordner_ids: number[];
  sordner_ids: number[];
}

interface DocumentTemplateMetaFieldsProps {
  value: DocumentTemplateMetaValue;
  onChange: (next: DocumentTemplateMetaValue) => void;
  disabled?: boolean;
}

function normalizeTags(raw: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of raw.split(/[,;\n]+/)) {
    const tag = part.trim();
    if (!tag) continue;
    const key = tag.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tag.slice(0, 80));
    if (out.length >= 30) break;
  }
  return out;
}

function toggleId(list: number[], id: number): number[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

export function DocumentTemplateMetaFields({
  value,
  onChange,
  disabled = false,
}: DocumentTemplateMetaFieldsProps) {
  const [tagDraft, setTagDraft] = useState(value.schlagworte.join(", "));
  const [jahre, setJahre] = useState<number[]>([]);
  const [jahr, setJahr] = useState<number | "all">("all");
  const [ordner, setOrdner] = useState<OrdnerOut[]>([]);
  const [sordner, setSordner] = useState<SOrdnerOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [ordnerQuery, setOrdnerQuery] = useState("");

  useEffect(() => {
    setTagDraft(value.schlagworte.join(", "));
  }, [value.schlagworte]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const years = await listYears();
        if (cancelled) return;
        setJahre(years || []);
      } catch {
        if (!cancelled) setJahre([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const [o, s] = await Promise.all([
          listOrdner(jahr === "all" ? undefined : jahr),
          listSOrdner(),
        ]);
        if (cancelled) return;
        setOrdner(o || []);
        setSordner(s || []);
      } catch {
        if (!cancelled) {
          setOrdner([]);
          setSordner([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jahr]);

  const filteredOrdner = useMemo(() => {
    const q = ordnerQuery.trim().toLowerCase();
    if (!q) return ordner;
    return ordner.filter((o) =>
      [o.nummer, o.beschreibung, o.kunden_name]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [ordner, ordnerQuery]);

  const filteredSOrdner = useMemo(() => {
    const q = ordnerQuery.trim().toLowerCase();
    if (!q) return sordner;
    return sordner.filter((o) =>
      [o.nummer, o.beschreibung, o.kunden_name]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [ordnerQuery, sordner]);

  const selectedLabels = useMemo(() => {
    const labels: string[] = [];
    for (const id of value.ordner_ids) {
      const hit = ordner.find((o) => o.id === id);
      labels.push(hit ? `Ordner ${hit.nummer}` : `Ordner #${id}`);
    }
    for (const id of value.sordner_ids) {
      const hit = sordner.find((o) => o.id === id);
      labels.push(hit ? `S-Ordner ${hit.nummer}` : `S-Ordner #${id}`);
    }
    return labels;
  }, [ordner, sordner, value.ordner_ids, value.sordner_ids]);

  return (
    <div className="space-y-3">
      <TextInput
        label="Schlagworte"
        value={tagDraft}
        disabled={disabled}
        onChange={(e) => setTagDraft(e.target.value)}
        onBlur={() => onChange({ ...value, schlagworte: normalizeTags(tagDraft) })}
        placeholder="z. B. Tür, Sanitär, Wartung"
        hint="Mit Komma trennen. Hilft bei der Suche in der Vorlagenliste."
      />

      <div className="space-y-2">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-label">Ordner zuweisen</p>
            <p className="text-xs text-label-secondary">
              Passende Vorlagen erscheinen zuerst, wenn ein Angebot in diesem Ordner geschrieben wird.
            </p>
          </div>
          <label className="text-xs text-label-secondary">
            Jahr{" "}
            <select
              className="eh-input ml-1 py-1 text-sm"
              value={jahr === "all" ? "all" : String(jahr)}
              disabled={disabled}
              onChange={(e) => {
                const next = e.target.value;
                setJahr(next === "all" ? "all" : Number(next));
              }}
            >
              <option value="all">Alle</option>
              {jahre.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
        </div>

        {selectedLabels.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {selectedLabels.map((label) => (
              <span key={label} className="rounded-full bg-fill-secondary px-2 py-0.5 text-xs text-label">
                {label}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-label-tertiary">Noch keine Ordner zugewiesen.</p>
        )}

        <input
          type="search"
          className="eh-input"
          value={ordnerQuery}
          disabled={disabled}
          onChange={(e) => setOrdnerQuery(e.target.value)}
          placeholder="Ordner suchen …"
        />

        {loading ? (
          <div className="flex items-center gap-2 py-3 text-sm text-label-secondary">
            <Spinner className="h-4 w-4" />
            Ordner werden geladen …
          </div>
        ) : (
          <div className="max-h-44 space-y-3 overflow-auto rounded-ios border border-separator p-2">
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                Projektordner
              </p>
              {filteredOrdner.length === 0 ? (
                <p className="text-xs text-label-tertiary">Keine Ordner gefunden.</p>
              ) : (
                <ul className="space-y-1">
                  {filteredOrdner.map((o) => {
                    const checked = value.ordner_ids.includes(o.id);
                    return (
                      <li key={`o-${o.id}`}>
                        <label className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 text-sm hover:bg-fill-secondary">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={checked}
                            disabled={disabled}
                            onChange={() =>
                              onChange({ ...value, ordner_ids: toggleId(value.ordner_ids, o.id) })
                            }
                          />
                          <span className="min-w-0">
                            <span className="font-medium text-label">{o.nummer}</span>
                            <span className="block truncate text-xs text-label-secondary">
                              {[o.beschreibung, o.kunden_name].filter(Boolean).join(" · ")}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                S-Ordner
              </p>
              {filteredSOrdner.length === 0 ? (
                <p className="text-xs text-label-tertiary">Keine S-Ordner gefunden.</p>
              ) : (
                <ul className="space-y-1">
                  {filteredSOrdner.map((o) => {
                    const checked = value.sordner_ids.includes(o.id);
                    return (
                      <li key={`s-${o.id}`}>
                        <label className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 text-sm hover:bg-fill-secondary">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={checked}
                            disabled={disabled}
                            onChange={() =>
                              onChange({ ...value, sordner_ids: toggleId(value.sordner_ids, o.id) })
                            }
                          />
                          <span className="min-w-0">
                            <span className="font-medium text-label">{o.nummer}</span>
                            <span className="block truncate text-xs text-label-secondary">
                              {[o.beschreibung, o.kunden_name].filter(Boolean).join(" · ")}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
